import type { GlobalThemeOverrides } from 'naive-ui'
import type { ThemeMode } from '@/composables/useTheme'

/**
 * Все цвета — резолвленные hex. Naive UI пропускает большинство common.*Color
 * полей через seemly/rgba для генерации hover/pressed/disabled-derivatives,
 * а seemly не умеет парсить `var(--xxx)` и кидает Invalid color value →
 * Input/InputNumber/DatePicker/DataTable рендерятся со сломанной внутренностью.
 *
 * Поэтому CSS-vars остаются только в наших собственных компонентах и
 * глобальных стилях, а Naive UI получает hex напрямую. Значения дублируют
 * tokens.css (источник правды). При изменении палитры — обновить и здесь.
 */
const COLORS = {
  light: {
    // brand
    primaryColor:        '#E2613E',
    primaryColorHover:   '#C24E2D',
    primaryColorPressed: '#C24E2D',
    primaryColorSuppl:   '#C24E2D',
    errorColor:          '#C84B4B',
    errorColorHover:     '#B43A3A',
    errorColorPressed:   '#B43A3A',
    errorColorSuppl:     '#B43A3A',
    warningColor:        '#C98724',
    warningColorHover:   '#B27317',
    warningColorPressed: '#B27317',
    warningColorSuppl:   '#B27317',
    successColor:        '#2C9E64',
    successColorHover:   '#218551',
    successColorPressed: '#218551',
    successColorSuppl:   '#218551',
    infoColor:           '#4A7BD3',
    infoColorHover:      '#3866BD',
    infoColorPressed:    '#3866BD',
    infoColorSuppl:      '#3866BD',
    // surfaces
    bodyColor:    '#EFEDE5',
    cardColor:    '#FFFFFF',
    modalColor:   '#FFFFFF',
    popoverColor: '#FFFFFF',
    tableColor:   '#FFFFFF',
    inputColor:   '#FFFFFF',
    actionColor:  '#FAF8F2',
    hoverColor:   '#FAF8F2',
    pressedColor: '#EFEDE5',
    // lines
    borderColor:  '#E5E1D6',
    dividerColor: '#E5E1D6',
    // text
    textColorBase:    '#1A1815',
    textColor1:       '#1A1815',
    textColor2:       '#3A352E',
    textColor3:       '#6E685D',
    textColorDisabled:'#97907F',
    placeholderColor: '#6E685D',
    closeIconColor:   '#6E685D',
    iconColor:        '#6E685D',
  },
  dark: {
    primaryColor:        '#F07A55',
    primaryColorHover:   '#E2613E',
    primaryColorPressed: '#E2613E',
    primaryColorSuppl:   '#E2613E',
    errorColor:          '#E66464',
    errorColorHover:     '#D14E4E',
    errorColorPressed:   '#D14E4E',
    errorColorSuppl:     '#D14E4E',
    warningColor:        '#E2A23E',
    warningColorHover:   '#C98724',
    warningColorPressed: '#C98724',
    warningColorSuppl:   '#C98724',
    successColor:        '#3DD17C',
    successColorHover:   '#2C9E64',
    successColorPressed: '#2C9E64',
    successColorSuppl:   '#2C9E64',
    infoColor:           '#6B9CE8',
    infoColorHover:      '#4A7BD3',
    infoColorPressed:    '#4A7BD3',
    infoColorSuppl:      '#4A7BD3',
    bodyColor:    '#14171D',
    cardColor:    '#181B22',
    modalColor:   '#181B22',
    popoverColor: '#181B22',
    tableColor:   '#181B22',
    inputColor:   '#181B22',
    actionColor:  '#1F232B',
    hoverColor:   '#1F232B',
    pressedColor: '#14171D',
    borderColor:  '#262A33',
    dividerColor: '#262A33',
    textColorBase:    '#E8E5DF',
    textColor1:       '#E8E5DF',
    textColor2:       '#C9C5BC',
    textColor3:       '#8B8678',
    textColorDisabled:'#6D6A60',
    placeholderColor: '#8B8678',
    closeIconColor:   '#8B8678',
    iconColor:        '#8B8678',
  },
} as const

export function buildThemeOverrides(mode: ThemeMode = 'light'): GlobalThemeOverrides {
  const colors = COLORS[mode]
  return {
    common: {
      ...colors,
      // non-color поля — CSS-vars безопасны, seemly не зовётся на них
      borderRadius:      'var(--r-ctrl)',
      borderRadiusSmall: 'var(--r-chip)',
      fontFamily:     "'Inter', system-ui, -apple-system, sans-serif",
      fontFamilyMono: "'JetBrains Mono', ui-monospace, monospace",
      fontSize:       'var(--fs-base)',
    },
  }
}
